import { useEffect, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, ChevronDown, KeyRound, Plus, ShieldCheck, X } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { SegmentedToggle } from '@/components/pages/calendar/facet-filter';
import { PreferenceRow } from '@/components/settings/settings-controls';
import { Spinner } from '@/components/ui/spinner';
import { DoctorProfilePreview } from '@/components/common/doctor-profile-preview';
import { ProfileEditor } from '@/components/common/profile-editor';
import { ImagePicker } from '@/components/common/image-picker';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/messages/avatar-color';
import { initialsFromLabel } from '@/lib/messages/messages-helpers';
import { useIsAdmin } from '@/hooks/use-permissions';
import { createStaff, fetchStaffUser, fetchTeam, fetchTeams, updateStaff } from '@/lib/api/team';
import { fetchWorkRoles } from '@/lib/api/work-plan';

const USER_TYPE_SEGMENTS = [
  { id: 'coordinator', label: 'Coordinator' },
  { id: 'doctor', label: 'Doctor' },
];

const TABS = [
  { id: 'account', label: 'Account' },
  { id: 'profile', label: 'Profile' },
];

/**
 * Bust the avatar cache after a save. The URL's HMAC covers (userId, hour bucket) only and the
 * endpoint reads just `v` and `t`, so an extra param is ignored server-side — but the response is
 * `private, max-age=1800`, so without this a changed photo would take up to 30 minutes to appear.
 */
function withFreshAvatar(staffUser) {
  if (!staffUser?.avatarUrl) return staffUser;
  const separator = staffUser.avatarUrl.includes('?') ? '&' : '?';
  return { ...staffUser, avatarUrl: `${staffUser.avatarUrl}${separator}cb=${Date.now()}` };
}

/** Long edge the profile photo is downscaled to; the server re-encodes at the same bound. */
const AVATAR_MAX_DIM = 512;

// Every profile field the dialog owns. Save sends only the ones that differ from the hydrated
// baseline — omitting a field leaves it alone, whereas sending it empty CLEARS it server-side.
// phones/emails are Account-tab fields, not profile, but they are detail-hydrated and ride the
// same changed-only contract (each list replaces the stored one wholesale when sent).
const PROFILE_FIELD_KEYS = [
  'firstName', 'lastName', 'photoUrl', 'bio', 'bioPhoto',
  'headline', 'specialties', 'qualifications', 'clinicalInterests', 'languages', 'quote', 'awards',
  'orderIndex', 'childrensCare',
  'phones', 'emails',
];

// `htmlFor` is opt-in: the composite controls here (SegmentedToggle, MultiSelect) are not form
// elements, and a <label> pointing at nothing is a11y noise — those render a <span> instead.
function Field({ label, children, hint, htmlFor }) {
  const Label = htmlFor ? 'label' : 'span';
  return (
    <div className="space-y-2">
      <Label
        htmlFor={htmlFor}
        className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {label}
      </Label>
      {children}
      {hint ? <p className="text-[11px] leading-snug text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

// Editable phone list (number + label per row). Rows the user leaves fully empty are skipped
// server-side, so an untouched "Add phone" row never blocks a save. Contact details are
// detail-hydrated like the profile fields — the caller disables this until the hydrate lands.
function PhoneListEditor({ phones, onChange, disabled }) {
  const update = (index, partial) => {
    onChange(phones.map((p, i) => (i === index ? { ...p, ...partial } : p)));
  };
  return (
    <div className="space-y-2">
      {phones.map((p, i) => (
        // Index keys are safe here: rows are fully controlled, so a removal re-renders the
        // survivors with their own values.
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="flex items-center gap-2">
          <Input
            value={p.number}
            onChange={(e) => update(i, { number: e.target.value })}
            placeholder="+65 9123 4567"
            inputMode="tel"
            disabled={disabled}
            className="flex-1"
            aria-label={`Phone number ${i + 1}`}
          />
          <Input
            value={p.label}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder="Label"
            disabled={disabled}
            className="w-32"
            aria-label={`Phone label ${i + 1}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => onChange(phones.filter((_, j) => j !== i))}
            disabled={disabled}
            aria-label={`Remove phone ${i + 1}`}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...phones, { number: '', label: '' }])}
        disabled={disabled}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Add phone
      </Button>
    </div>
  );
}

// Editable email list — same contract as PhoneListEditor, one address per row.
function EmailListEditor({ emails, onChange, disabled }) {
  const update = (index, partial) => {
    onChange(emails.map((e, i) => (i === index ? { ...e, ...partial } : e)));
  };
  return (
    <div className="space-y-2">
      {emails.map((entry, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="flex items-center gap-2">
          <Input
            value={entry.email}
            onChange={(e) => update(i, { email: e.target.value })}
            placeholder="name@clinic.example"
            inputMode="email"
            disabled={disabled}
            className="flex-1"
            aria-label={`Email address ${i + 1}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => onChange(emails.filter((_, j) => j !== i))}
            disabled={disabled}
            aria-label={`Remove email ${i + 1}`}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...emails, { email: '' }])}
        disabled={disabled}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Add email
      </Button>
    </div>
  );
}

// Generic multi-select as a Popover checklist; the selection renders as chips on the trigger.
// Options: [{id, name, description?}]. Shared by the access-groups and teams pickers.
// The list is height-capped and scrolls (real databases carry dozens of auto-created teams —
// an uncapped popover overflowed the viewport). `resolveLabel` names selected ids that are
// not in the current options page (server-side search only returns a window), falling back
// to the raw id so a chip is never blank. `onSearchChange` (with `searchValue`) renders a
// search box wired to the caller — server-side narrowing, not just a client filter.
function MultiSelect({
  options,
  selectedIds,
  onChange,
  disabled,
  placeholder,
  emptyHint,
  resolveLabel,
  searchValue,
  onSearchChange,
  searchPlaceholder,
}) {
  const [open, setOpen] = useState(false);
  const selected = selectedIds.map((id) => {
    const option = options.find((o) => o.id === id);
    if (option) return option;
    const label = resolveLabel ? resolveLabel(id) : id;
    return { id, name: label || id };
  });
  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-left text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            selected.map((o) => (
              <Badge key={o.id} variant="secondary" className="max-w-48 truncate text-[11px]">
                {o.name}
              </Badge>
            ))
          )}
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-1.5">
        {onSearchChange ? (
          <div className="pb-1.5">
            <Input
              value={searchValue ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder || 'Search…'}
              className="h-8 text-[13px]"
            />
          </div>
        ) : null}
        <ScrollArea viewportClassName="max-h-64">
          {options.length === 0 ? (
            <div className="px-2 py-2 text-[12px] italic text-muted-foreground">{emptyHint}</div>
          ) : (
            options.map((o) => {
              const checked = selectedIds.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent"
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                    )}
                  >
                    {checked ? <Check className="h-3 w-3" aria-hidden /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className={cn('block truncate font-medium', !o.hasName && o.hasName !== undefined && 'font-mono text-[12px]')}>
                      {o.name}
                    </span>
                    {o.description ? (
                      <span className="block truncate text-[11px] text-muted-foreground">{o.description}</span>
                    ) : null}
                  </span>
                </button>
              );
            })
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function fromStaffUser(user) {
  return {
    displayName: String(user?.displayName || '').trim(),
    loginId: String(user?.loginId || '').trim(),
    userType: user?.userType === 'doctor' ? 'doctor' : 'coordinator',
    workRole: String(user?.workRole || ''),
    hiddenFromStaffList: Boolean(user?.hiddenFromStaffList),
    active: user?.status !== 'inactive',
    isAdmin: Boolean(user?.isAdmin),
    permissionGroupIds: Array.isArray(user?.permissionGroupIds) ? [...user.permissionGroupIds] : [],
    teamIds: Array.isArray(user?.teamIds) ? [...user.teamIds] : [],
    password: '',
    // Profile fields stay blank until the detail hydrate lands — the list row does not carry them.
    firstName: String(user?.firstName || '').trim(),
    lastName: String(user?.lastName || '').trim(),
    photoUrl: '',
    bio: '',
    bioPhoto: '',
    phones: [],
    emails: [],
    headline: '',
    specialties: [],
    qualifications: [],
    clinicalInterests: [],
    languages: [],
    quote: '',
    awards: [],
    orderIndex: '',
    childrensCare: false,
  };
}

const EMPTY_FORM = {
  displayName: '',
  loginId: '',
  userType: 'coordinator',
  workRole: '',
  hiddenFromStaffList: false,
  active: true,
  isAdmin: false,
  permissionGroupIds: [],
  teamIds: [],
  password: '',
  firstName: '',
  lastName: '',
  photoUrl: '',
  bio: '',
  bioPhoto: '',
  phones: [],
  emails: [],
  headline: '',
  specialties: [],
  qualifications: [],
  clinicalInterests: [],
  languages: [],
  quote: '',
  awards: [],
  orderIndex: '',
  childrensCare: false,
};

/**
 * Create/edit dialog for one employee (Settings → Team → Employees). One dialog serves both
 * modes — unlike the patients editor the two forms are near-identical (`user` null = create).
 * Server-side guard rails still apply: only admins can flip isAdmin or edit an admin account,
 * and demoting the tenant's last active admin is refused (409) — errors surface as toasts.
 */
export function StaffEditorDialog({ open, onOpenChange, user, groups = [], teams = [], onSaved }) {
  const isEdit = Boolean(user?.id);
  const callerIsAdmin = useIsAdmin();
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  // Renaming the sign-in credential is behind an explicit reveal, like the password reset: the
  // field sits beside Display name, and a stray keystroke there would lock the employee out.
  const [editLoginId, setEditLoginId] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tab, setTab] = useState('account');
  const tabPanelId = useId();
  // Gate on the detail hydrate. The list row carries no photo/bio, so the form holds empty strings
  // until GET /api/admin/staff/{id} lands — and an empty string CLEARS the field on save. Sending
  // profile fields before this is true would silently wipe an employee's photo and bio.
  const [profileHydrated, setProfileHydrated] = useState(false);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  // Snapshot of the hydrated profile fields, so save can send only what actually changed. This is
  // not just an optimisation: form.photoUrl holds the ticket URL for a stored photo (that is what
  // renders it), and echoing that back would fail the server's base64 decode. Re-sending an
  // untouched bio photo would also push it through a second lossy JPEG encode.
  const [profileBaseline, setProfileBaseline] = useState(null);
  // Teams picker state: the /api/teams listing is capped at 50, so the picker searches
  // server-side as you type. knownTeams accumulates every team ever seen (state, not a ref —
  // chips must re-render when a late name resolution lands) so selected teams keep their
  // names even when the search window moves past them.
  const [teamQuery, setTeamQuery] = useState('');
  const [teamOptions, setTeamOptions] = useState(teams);
  const [knownTeams, setKnownTeams] = useState(() => new Map());
  // The work-role vocabulary is configurable (Settings → Work roles), so the segmented
  // control is built from the server list rather than a fixed set. A failed load leaves it
  // empty and the control hides — the field is optional, and guessing ids would be wrong.
  const [workRoles, setWorkRoles] = useState([]);
  const mergeKnownTeams = (items) => {
    if (!items.length) return;
    setKnownTeams((prev) => {
      const next = new Map(prev);
      for (const t of items) next.set(t.id, t);
      return next;
    });
  };

  // Reset keyed on the IDENTITY being edited, never on the `teams` prop: that is an array whose
  // reference changes on any parent refetch, and re-running this would discard whatever the user
  // has typed (including an unsaved photo worth hundreds of kB).
  const userId = user?.id;
  useEffect(() => {
    if (!open) return;
    setForm(userId ? fromStaffUser(user) : { ...EMPTY_FORM });
    setShowPasswordReset(false);
    setEditLoginId(false);
    setTeamQuery('');
    setTab('account');
    // A create has nothing to hydrate, so its profile fields are editable immediately.
    setProfileHydrated(!userId);
    setProfileLoadFailed(false);
    setProfileBaseline(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `user` is re-read intentionally; the
    // identity (open + id) is what should trigger a reset.
  }, [open, userId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchWorkRoles()
      .then((res) => {
        if (!cancelled) setWorkRoles(Array.isArray(res?.roles) ? res.roles : []);
      })
      .catch(() => {
        /* optional field — leave the control hidden rather than guessing the vocabulary */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // "Default" rather than "None": an employee with no explicit role shows the FIRST
  // configured role everywhere in the planner (label and filter alike), so calling it
  // "None" here would contradict what the roster displays.
  const workRoleSegments = useMemo(
    () => [
      { id: 'none', label: workRoles.length > 0 ? `Default (${workRoles[0].label})` : 'Default' },
      ...workRoles.map((r) => ({ id: r.id, label: r.label })),
    ],
    [workRoles],
  );

  // Seed/refresh the teams picker independently of the form reset above.
  useEffect(() => {
    if (!open) return;
    setTeamOptions(teams);
    mergeKnownTeams(teams);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mergeKnownTeams is a stable setState wrapper.
  }, [open, teams]);

  // Hydrate the full record: the directory list omits photo, bio and bio photo by design.
  useEffect(() => {
    if (!open || !userId) return undefined;
    let cancelled = false;
    fetchStaffUser({ userId })
      .then((detail) => {
        if (cancelled) return;
        const profile = {
          firstName: detail.firstName,
          lastName: detail.lastName,
          // The ticket URL is what renders the stored photo; a fresh pick replaces it with a data URI.
          photoUrl: detail.avatarUrl,
          bio: detail.bio,
          bioPhoto: detail.bioPhoto,
          headline: detail.headline,
          specialties: detail.specialties,
          qualifications: detail.qualifications,
          clinicalInterests: detail.clinicalInterests,
          languages: detail.languages,
          quote: detail.quote,
          awards: detail.awards,
          phones: detail.phones,
          emails: detail.emails,
          // Form-typed (string / bool) so the baseline diff compares like-for-like.
          orderIndex: detail.orderIndex == null ? '' : String(detail.orderIndex),
          childrensCare: Boolean(detail.childrensCare),
        };
        setForm((prev) => ({ ...prev, ...profile }));
        setProfileBaseline(profile);
        setProfileHydrated(true);
      })
      .catch(() => {
        if (cancelled) return;
        setProfileLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  // Debounced server-side team search while the dialog is open (empty query = first page).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const handle = setTimeout(() => {
      fetchTeams({ q: teamQuery })
        .then((res) => {
          if (cancelled) return;
          mergeKnownTeams(res.items);
          setTeamOptions(res.items);
        })
        .catch(() => {
          /* keep the current options; the picker still works from the last page */
        });
    }, teamQuery.trim() ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, teamQuery]);

  // Resolve names for selected teams the search window hasn't surfaced (e.g. beyond the
  // 50-result page): fetch them individually once per dialog open.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const missing = form.teamIds.filter((id) => !knownTeams.has(id));
    if (missing.length === 0) return undefined;
    Promise.allSettled(missing.map((teamId) => fetchTeam({ teamId }))).then((results) => {
      if (cancelled) return;
      mergeKnownTeams(results.filter((r) => r.status === 'fulfilled').map((r) => r.value));
    });
    return () => {
      cancelled = true;
    };
  }, [open, form.teamIds, knownTeams]);

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  // Case-insensitive, because the server stores login IDs lowercased — re-typing "Jane@x" over a
  // stored "jane@x" is not an edit, and sending it would spend a collision check to change nothing.
  const loginIdChanged =
    isEdit &&
    editLoginId &&
    form.loginId.trim().toLowerCase() !== String(user?.loginId || '').trim().toLowerCase();

  // Non-admin staff managers cannot touch admin accounts at all (server enforces the same).
  const targetLocked = isEdit && form.isAdmin && !callerIsAdmin && Boolean(user?.isAdmin);

  const canSubmit = useMemo(() => {
    if (isSaving) return false;
    if (!isEdit && (!form.loginId.trim() || !form.password.trim())) return false;
    // A revealed-but-emptied login ID is a lockout, not a clear — the server 400s on it anyway.
    if (isEdit && editLoginId && !form.loginId.trim()) return false;
    return true;
  }, [isSaving, isEdit, editLoginId, form.loginId, form.password]);

  // Only the profile fields the user actually changed. Omitting a field leaves it untouched;
  // sending "" clears it — so an unloaded or unchanged field must never be sent.
  const changedProfileFields = () => {
    if (!profileHydrated || !profileBaseline) return {};
    const changed = {};
    for (const key of PROFILE_FIELD_KEYS) {
      // Arrays/objects compare by value: a re-render alone must not look like an edit, and an
      // unchanged field must stay OUT of the payload (sending it empty would clear it server-side).
      const a = form[key];
      const b = profileBaseline[key];
      const differs = typeof a === 'object' ? JSON.stringify(a) !== JSON.stringify(b) : a !== b;
      if (differs) changed[key] = a;
    }
    return changed;
  };

  const handleSave = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    try {
      if (isEdit) {
        const updated = await updateStaff({
          userId: user.id,
          displayName: form.displayName,
          userType: form.userType,
          workRole: form.userType === 'doctor' ? '' : form.workRole,
          hiddenFromStaffList: form.hiddenFromStaffList,
          status: form.active ? 'active' : 'inactive',
          // Only send isAdmin when the caller may change it — the server 403s otherwise.
          ...(callerIsAdmin ? { isAdmin: form.isAdmin } : {}),
          permissionGroupIds: form.permissionGroupIds,
          teamIds: form.teamIds,
          ...(loginIdChanged ? { loginId: form.loginId } : {}),
          ...(showPasswordReset && form.password.trim() ? { password: form.password } : {}),
          ...changedProfileFields(),
        });
        toast.success('Employee saved.');
        onSaved?.(withFreshAvatar(updated));
      } else {
        const created = await createStaff({
          loginId: form.loginId,
          password: form.password,
          displayName: form.displayName,
          userType: form.userType,
          workRole: form.userType === 'doctor' ? '' : form.workRole,
          hiddenFromStaffList: form.hiddenFromStaffList,
          status: form.active ? 'active' : 'inactive',
          isAdmin: callerIsAdmin ? form.isAdmin : false,
          permissionGroupIds: form.permissionGroupIds,
          teamIds: form.teamIds,
          firstName: form.firstName,
          lastName: form.lastName,
          photoUrl: form.photoUrl,
          bio: form.bio,
          bioPhoto: form.bioPhoto,
          headline: form.headline,
          specialties: form.specialties,
          qualifications: form.qualifications,
          clinicalInterests: form.clinicalInterests,
          languages: form.languages,
          quote: form.quote,
          awards: form.awards,
          orderIndex: form.orderIndex,
          childrensCare: form.childrensCare,
          phones: form.phones,
          emails: form.emails,
        });
        toast.success('Employee created.');
        onSaved?.(withFreshAvatar(created));
      }
      onOpenChange?.(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save employee');
    } finally {
      setIsSaving(false);
    }
  };

  const heroName =
    form.displayName.trim() ||
    [form.firstName, form.lastName].map((p) => p.trim()).filter(Boolean).join(' ') ||
    form.loginId.trim() ||
    'New employee';
  const profileLocked = targetLocked || !profileHydrated;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
          'flex max-h-[min(90vh,760px)] flex-col gap-0 overflow-hidden p-0',
          // The Profile tab carries a live patient-view preview beside the form; Account does not.
          tab === 'profile' ? 'sm:max-w-5xl' : 'sm:max-w-2xl',
        )}>
        {/* gap-0 p-0 are load-bearing: DialogContent defaults to `grid … gap-4 p-6`, which fights
            this flex column. Each region owns its own px-6 instead. */}
        <DialogHeader className="shrink-0 space-y-0 border-b border-border/60 bg-muted/30 px-6 pb-4 pt-5 text-left">
          <DialogTitle className="sr-only">{isEdit ? `Edit employee — ${heroName}` : 'Add employee'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit
              ? 'Account, role, access-group assignment and profile.'
              : 'Creates a staff account in your clinic. Access comes from the assigned groups.'}
          </DialogDescription>
          {/* pr-10 keeps the text clear of the absolutely-positioned close button. */}
          <div className="flex items-start gap-3 pr-10">
            <Avatar className="h-14 w-14 shrink-0 ring-1 ring-border/60">
              {form.photoUrl ? <AvatarImage src={form.photoUrl} alt="" /> : null}
              <AvatarFallback className={cn('text-base font-semibold', getAvatarColorClasses(user?.id || form.loginId))}>
                {initialsFromLabel(heroName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="truncate text-lg font-semibold leading-tight tracking-tight" title={heroName}>
                {heroName}
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {form.isAdmin ? (
                  <Badge
                    className="gap-1 border-amber-200 bg-amber-50 text-[10px] text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-300"
                    variant="secondary"
                  >
                    <ShieldCheck className="h-3 w-3" /> Admin
                  </Badge>
                ) : null}
                <Badge variant="secondary" className="text-[10px] capitalize">
                  {form.userType}
                </Badge>
                <Badge
                  variant="outline"
                  className={cn(
                    'text-[10px]',
                    form.active
                      ? 'border-emerald-300 text-emerald-700 dark:border-emerald-500/40 dark:text-emerald-300'
                      : 'text-muted-foreground',
                  )}
                >
                  {form.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div
          role="tablist"
          aria-label="Employee editor sections"
          className="flex shrink-0 items-center gap-1 border-b border-border bg-background px-6"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              aria-controls={`${tabPanelId}-${t.id}`}
              onClick={() => setTab(t.id)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                tab === t.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* The Profile tab is form | live preview; the preview column does not scroll and
            collapses below lg, where the form takes the full width. */}
        <div className="flex min-h-0 flex-1">
          <ScrollArea className="min-h-0 flex-1" viewportClassName="px-6 pb-7 pt-4">
            {/* pr-3 keeps content clear of the scrollbar. */}
            <div role="tabpanel" id={`${tabPanelId}-${tab}`} className="space-y-5 pr-3">
            {tab === 'account' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Display name" htmlFor="staff-display-name">
                    <Input
                      id="staff-display-name"
                      value={form.displayName}
                      onChange={(e) => patch({ displayName: e.target.value })}
                      placeholder="e.g. Nurse Wong"
                      disabled={targetLocked}
                    />
                  </Field>
                  <Field
                    label="Login ID"
                    htmlFor="staff-login-id"
                    hint={
                      !isEdit
                        ? 'Used to sign in; usually an email.'
                        : editLoginId
                          ? 'This employee signs in with the new ID from their next sign-in; saved sessions stay valid.'
                          : undefined
                    }
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        id="staff-login-id"
                        value={form.loginId}
                        onChange={(e) => patch({ loginId: e.target.value })}
                        placeholder="name@clinic.example"
                        // Locked until the reveal below, and always for an account this caller
                        // may not touch at all.
                        disabled={isEdit && (!editLoginId || targetLocked)}
                        autoComplete="off"
                        className={cn('flex-1', isEdit && !editLoginId && 'font-mono text-xs')}
                      />
                      {isEdit ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={targetLocked}
                          onClick={() => {
                            // Cancelling restores the stored ID: the input is the same control
                            // either way, so an abandoned edit must not survive as a pending save.
                            if (editLoginId) patch({ loginId: String(user?.loginId || '') });
                            setEditLoginId((v) => !v);
                          }}
                          className="shrink-0 px-2 text-xs"
                        >
                          {editLoginId ? 'Cancel' : 'Change'}
                        </Button>
                      ) : null}
                    </div>
                  </Field>
                </div>

                <Field
                  label="Phone numbers"
                  hint={
                    profileLoadFailed
                      ? 'Could not load contact details — reopen the dialog to edit them.'
                      : 'Staff directory contact numbers — not used for sign-in.'
                  }
                >
                  <PhoneListEditor
                    phones={form.phones}
                    onChange={(next) => patch({ phones: next })}
                    disabled={profileLocked}
                  />
                </Field>

                <Field
                  label="Email addresses"
                  hint="Contact emails — the login ID above stays the sign-in credential."
                >
                  <EmailListEditor
                    emails={form.emails}
                    onChange={(next) => patch({ emails: next })}
                    disabled={profileLocked}
                  />
                </Field>

                <Field label="Role" hint="Doctors appear in the clinical directory; coordinators do not.">
                  <SegmentedToggle
                    value={form.userType}
                    segments={USER_TYPE_SEGMENTS}
                    onChange={(id) => patch({ userType: id })}
                    ariaLabel="Employee role"
                  />
                </Field>

                {form.userType !== 'doctor' && workRoleSegments.length > 1 ? (
                  <Field
                    label="Work role"
                    hint="Groups this person in the Work Plan roster (role pills, MC cards). Edit the list in Settings → Work roles."
                  >
                    <SegmentedToggle
                      value={form.workRole || 'none'}
                      segments={workRoleSegments}
                      onChange={(id) => patch({ workRole: id === 'none' ? '' : id })}
                      ariaLabel="Work role"
                    />
                  </Field>
                ) : null}

                <div className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border bg-card/40">
                  <PreferenceRow
                    title="Active"
                    description="Inactive accounts cannot sign in."
                    checked={form.active}
                    onChange={(v) => patch({ active: v })}
                    disabled={targetLocked}
                    className="px-3 py-2.5"
                  />
                  <PreferenceRow
                    title="Admin"
                    description="Full access to every module, bypassing access groups."
                    checked={form.isAdmin}
                    onChange={(v) => patch({ isAdmin: v })}
                    disabled={!callerIsAdmin}
                    className="px-3 py-2.5"
                  />
                  <PreferenceRow
                    title="Hide from staff list"
                    description="Keeps this account out of the Employees list and the Work Plan roster. Sign-in and everything else are unaffected."
                    checked={form.hiddenFromStaffList}
                    onChange={(v) => patch({ hiddenFromStaffList: v })}
                    className="px-3 py-2.5"
                  />
                </div>
                {form.isAdmin ? (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] leading-snug text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200">
                    Admins have full access to everything, including team management — groups below are ignored.
                  </p>
                ) : null}

                <Field label="Access groups" hint="Rights add up across groups; no groups means no gated areas.">
                  <MultiSelect
                    options={groups}
                    selectedIds={form.permissionGroupIds}
                    onChange={(ids) => patch({ permissionGroupIds: ids })}
                    disabled={targetLocked}
                    placeholder="No access groups"
                    emptyHint="No access groups yet — create one under Access groups."
                  />
                </Field>

                <Field label="Teams" hint="Routing teams: team-assigned conversations become visible to their members.">
                  <MultiSelect
                    options={teamOptions}
                    selectedIds={form.teamIds}
                    onChange={(ids) => patch({ teamIds: ids })}
                    disabled={targetLocked}
                    placeholder="No teams"
                    emptyHint={teamQuery.trim() ? 'No teams match this search.' : 'No teams yet — teams are created from conversation routing.'}
                    resolveLabel={(id) => knownTeams.get(id)?.name || id}
                    searchValue={teamQuery}
                    onSearchChange={setTeamQuery}
                    searchPlaceholder="Search teams by name…"
                  />
                </Field>

                {isEdit ? (
                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPasswordReset((v) => !v)}
                      disabled={targetLocked}
                    >
                      <KeyRound className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      {showPasswordReset ? 'Cancel password reset' : 'Reset password'}
                    </Button>
                    {showPasswordReset ? (
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) => patch({ password: e.target.value })}
                        placeholder="New password"
                        autoComplete="new-password"
                      />
                    ) : null}
                  </div>
                ) : (
                  <Field label="Password" htmlFor="staff-password">
                    <Input
                      id="staff-password"
                      type="password"
                      value={form.password}
                      onChange={(e) => patch({ password: e.target.value })}
                      placeholder="Initial password"
                      autoComplete="new-password"
                    />
                  </Field>
                )}
              </>
            ) : (
              <>
                {profileLoadFailed ? (
                  <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[11.5px] leading-snug text-destructive">
                    Could not load this employee&rsquo;s profile — reopen the dialog to edit it. Account
                    settings can still be saved.
                  </p>
                ) : null}

                {/* w-20 pins the avatar column: without it the picker takes half the row and
                    crushes the note beside it to one word per line. */}
                <div className="flex items-start gap-4">
                  <div className="w-20 shrink-0">
                    <ImagePicker
                      value={form.photoUrl}
                      onChange={(next) => patch({ photoUrl: next })}
                      shape="circle"
                      previewClassName="h-20 w-20"
                      maxDim={AVATAR_MAX_DIM}
                      label="Avatar"
                      disabled={profileLocked}
                    />
                  </div>
                  <p className="min-w-0 flex-1 pt-7 text-[11px] leading-relaxed text-muted-foreground">
                    Shown wherever this employee appears in lists and chats — separate from the
                    portrait below. Display name (Account tab) is what colleagues see internally;
                    everything below is what patients see in the Healo app.
                  </p>
                </div>

                <Separator />

                <ProfileEditor
                  firstName={form.firstName}
                  lastName={form.lastName}
                  onFirstNameChange={(next) => patch({ firstName: next })}
                  onLastNameChange={(next) => patch({ lastName: next })}
                  photo={form.bioPhoto}
                  onPhotoChange={(next) => patch({ bioPhoto: next })}
                  headline={form.headline}
                  onHeadlineChange={(next) => patch({ headline: next })}
                  specialties={form.specialties}
                  onSpecialtiesChange={(next) => patch({ specialties: next })}
                  qualifications={form.qualifications}
                  onQualificationsChange={(next) => patch({ qualifications: next })}
                  clinicalInterests={form.clinicalInterests}
                  onClinicalInterestsChange={(next) => patch({ clinicalInterests: next })}
                  languages={form.languages}
                  onLanguagesChange={(next) => patch({ languages: next })}
                  bio={form.bio}
                  onBioChange={(next) => patch({ bio: next })}
                  quote={form.quote}
                  onQuoteChange={(next) => patch({ quote: next })}
                  awards={form.awards}
                  onAwardsChange={(next) => patch({ awards: next })}
                  orderIndex={form.orderIndex}
                  onOrderIndexChange={(next) => patch({ orderIndex: next })}
                  childrensCare={form.childrensCare}
                  onChildrensCareChange={(next) => patch({ childrensCare: next })}
                  disabled={profileLocked}
                />
              </>
            )}
            </div>
          </ScrollArea>

          {tab === 'profile' ? (
            <aside className="hidden w-[330px] shrink-0 flex-col items-center overflow-hidden border-l border-border bg-gradient-to-b from-muted/40 to-muted/70 pb-1 pt-4 lg:flex">
              <div className="flex w-full items-center gap-2 px-4 pb-3">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Patient view
                </span>
                <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                  <span className="h-[5px] w-[5px] rounded-full bg-emerald-500" />
                  Live
                </span>
              </div>
              <DoctorProfilePreview
                firstName={form.firstName}
                lastName={form.lastName}
                displayName={form.displayName}
                photo={form.bioPhoto}
                headline={form.headline}
                specialties={form.specialties}
                qualifications={form.qualifications}
                clinicalInterests={form.clinicalInterests}
                languages={form.languages}
                bio={form.bio}
                quote={form.quote}
                awards={form.awards}
                seed={user?.id || form.loginId}
              />
              <p className="px-5 py-2.5 text-center text-[11px] leading-snug text-muted-foreground">
                Exactly what patients see in the Healo app.
                <br />
                Updates as you type; scroll inside to check the fold.
              </p>
            </aside>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 border-t border-border/60 bg-muted/20 px-6 py-3">
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSubmit || targetLocked}>
            {isSaving ? <Spinner className="mr-2 h-4 w-4" /> : null}
            {isEdit ? 'Save' : 'Create employee'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
